import { BrowserHistory, Location } from 'history';
import { QUERY_STRING } from '../../enums/QueryString';
import PageKey from '../../pages/PageKey';

/**
 * Strategy to render an array of routes that could be conditional, because
 * the <Routes> component expects only <Route> children and breaks if a
 * something else is used to do something like this:
 *    <Routes>
 *      {isLoggedIn && <Route path="/private" />}
 *      {!isLoggedIn && <Route path="/public" />}
 *    </Routes>
 * So an array is used to generate the different routes, where the array could
 * have any conditional logic to render or not the routes. Finally, the
 * routes are rendered by mapping the array and returning each specific route.
 */
export const renderRoutesArray = (routes: JSX.Element[]) =>
  routes.map((route) => route);

/**
 * Checks if a given section is the one currently active according to the current path.
 * This is basically used for organization sections, which always have routes like this:
 *  - /organization/{id}/{section}
 */
export const isSectionCurrentlyActive = (
  section: string,
  history: BrowserHistory
) => {
  const { pathname } = history.location;
  // @todo module federation, the section segment may not be at index 3
  const activePath = pathname.split('/')[3];
  return section === activePath;
};

export const getLoginPath = (location: Location): string => {
  const params = new URLSearchParams(location.search);
  const isSignOut = !!params.get(QUERY_STRING.SIGN_OUT);
  // Avoid the next location when it happens explicitly through the sign out flow
  const next = location.pathname;

  if (!isSignOut && next && !['', '/', `/${PageKey.Denied}`].includes(next)) {
    return `/${PageKey.LogIn}?${QUERY_STRING.NEXT}=${encodeURIComponent(next)}`;
  }

  return `/${PageKey.LogIn}${location.search}`;
};

const cleanPath = (path: string): string => path.replace(/^\//, '');

export const getNextPath = (location: Location): string | undefined => {
  const params = new URLSearchParams(location.search);
  const next = params.get(QUERY_STRING.NEXT);

  if (!next) {
    return undefined;
  }

  params.delete(QUERY_STRING.NEXT);
  // mocks a full URL with the given next path so we can easily extract
  // any query params that must be kept in the redirection
  const nextUrl = new URL(`${window.location.origin}/${cleanPath(next)}`);
  // and now, any other query params that were in the original URL are appended
  const newUrlParams = new URLSearchParams(nextUrl.search);
  params.forEach((value, key) => newUrlParams.set(key, value));
  // this way, the next path will have any forced query params + any other
  // from the original URL
  const cleanedNext = cleanPath(nextUrl.pathname);
  // e.g.: /organization/1/settings?forcedParam=foo&next=/organization/1/settings/integrations?pathParam=bar
  // result: /organization/1/settings/integrations?pathParam=bar&forcedParam=foo
  return `./${cleanedNext}?${newUrlParams.toString()}`;
};
